import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { NewsItem } from "@/mocks/types";
import { formatDate } from "@/utils/format";

interface HeroCarouselProps {
  items: NewsItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const timer = useRef<number | null>(null);

  const go = useCallback(
    (next: number) => {
      if (items.length === 0) return;
      setActive(((next % items.length) + items.length) % items.length);
    },
    [items.length],
  );

  useEffect(() => {
    if (items.length <= 1) return undefined;
    timer.current = window.setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 6500);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [items.length]);

  if (items.length === 0) {
    return (
      <section className="w-full h-[420px] md:h-[600px] bg-foreground-950 flex items-center justify-center">
        <div className="text-center px-6">
          <div className="w-16 h-16 mx-auto rounded-full bg-background-50/10 flex items-center justify-center text-background-300">
            <i className="ri-newspaper-line text-2xl" />
          </div>
          <p className="mt-4 text-background-200 text-sm md:text-base">暂无最新资讯</p>
        </div>
      </section>
    );
  }

  return (
    <section className="relative w-full h-[460px] md:h-[600px] lg:h-[640px] overflow-hidden bg-foreground-950">
      {items.map((item, idx) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-[900ms] ease-smooth ${
            idx === active ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={item.cover}
            alt={item.title}
            title={`${item.title} 焦点资讯`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/92 via-foreground-950/70 to-foreground-950/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-transparent to-foreground-950/40" />
        </div>
      ))}

      <div className="relative z-10 w-full h-full px-4 md:px-8 flex items-end md:items-center">
        <div className="w-full pb-20 md:pb-0 pt-16">
          <div className="max-w-2xl">
            {items.map((item, idx) => (
              <div
                key={item.id}
                className={idx === active ? "block animate-fade-up" : "hidden"}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="sr-only">91动漫</span>
                  <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-[11px] md:text-xs font-label flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-background-50 pulse-dot" />
                    焦点资讯
                  </span>
                  <span className="px-2.5 py-1 rounded-md bg-background-50/10 backdrop-blur-sm text-background-100 text-[11px] md:text-xs font-label">
                    {item.category}
                  </span>
                  <span className="text-[11px] md:text-xs text-background-300 font-label">
                    {formatDate(item.publishedAt)}
                  </span>
                </div>
                <h2 className="mt-4 font-heading font-bold text-background-50 text-2xl sm:text-3xl md:text-4xl lg:text-[44px] leading-[1.2] tracking-tight">
                  {item.title}
                </h2>
                <p className="mt-3 md:mt-4 text-background-200 text-sm md:text-base leading-relaxed max-w-xl line-clamp-2 md:line-clamp-3">
                  {item.summary}
                </p>
                <div className="mt-6 md:mt-8 flex items-center gap-3 flex-wrap">
                  <Link
                    to={`/news/detail/${item.id}`}
                    className="inline-flex items-center gap-2 h-11 px-5 md:px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors duration-200 cursor-pointer"
                  >
                    阅读全文
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-sm" />
                    </span>
                  </Link>
                  <Link
                    to="/news"
                    className="inline-flex items-center gap-2 h-11 px-5 md:px-6 rounded-md border border-background-50/25 hover:border-background-50/60 text-background-100 text-sm font-medium whitespace-nowrap backdrop-blur-sm transition-colors duration-200 cursor-pointer"
                  >
                    全部资讯
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="absolute z-10 bottom-5 md:bottom-8 left-4 md:left-8 right-4 md:right-8 flex items-end justify-between gap-4">
        <div className="flex items-center gap-2">
          {items.map((item, idx) => (
            <button
              key={item.id}
              type="button"
              onClick={() => go(idx)}
              aria-label={`切换到第 ${idx + 1} 条资讯`}
              className={`h-1 rounded-full transition-all duration-500 cursor-pointer ${
                idx === active ? "w-8 md:w-10 bg-primary-500" : "w-4 md:w-5 bg-background-50/35 hover:bg-background-50/60"
              }`}
            />
          ))}
          <span className="ml-2 text-[11px] font-label text-background-300">
            {String(active + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => go(active - 1)}
            aria-label="上一条"
            className="w-9 h-9 md:w-10 md:h-10 rounded-full border border-background-50/25 hover:border-background-50/60 text-background-100 flex items-center justify-center backdrop-blur-sm transition-colors duration-200 cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-lg" />
          </button>
          <button
            type="button"
            onClick={() => go(active + 1)}
            aria-label="下一条"
            className="w-9 h-9 md:w-10 md:h-10 rounded-full border border-background-50/25 hover:border-background-50/60 text-background-100 flex items-center justify-center backdrop-blur-sm transition-colors duration-200 cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-lg" />
          </button>
        </div>
      </div>
    </section>
  );
}